import CheckIcon from "@mui/icons-material/Check";
import {Box, FormControl, MenuItem, Select, Stack, TextField} from "@mui/material";
import {alpha, Theme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {surveyMarkdownToPlainText} from "../../shared/surveyMarkdownText.util";
import {RunnerQuestionProps} from "./runnerQuestion.types";
import {surveyControls} from "../surveyRunnerColors";

const OTHER_VALUE = '__other__';

const OTHER_MAX_LENGTH = 10000;

const OTHER_COUNTER_FROM = Math.floor(OTHER_MAX_LENGTH * 0.9);

const MENU_ROW_HEIGHT = 44;

const formatCount = (count: number, language: string): string => {
    try {
        return count.toLocaleString(language);
    } catch {
        return String(count);
    }
};

function DropdownQuestion({question, localize, language, value, onAnswerChange}: RunnerQuestionProps) {
    const {t} = useTranslation(undefined, {lng: language});

    const [localSelected, setLocalSelected] = useState('');
    const [localOtherText, setLocalOtherText] = useState('');

    const selectedFromValue = value && (value.otherSelected ? OTHER_VALUE : value.optionIds?.[0] ?? '');
    const selected = selectedFromValue ?? localSelected;
    const otherText = value?.customText ?? localOtherText;

    const select = (next: string) => {
        const otherActive = next === OTHER_VALUE;

        setLocalSelected(next);
        if (!otherActive) setLocalOtherText('');

        onAnswerChange?.({
            ...value,
            optionIds: otherActive || !next ? [] : [next],
            otherSelected: otherActive,
            customText: otherActive ? otherText : undefined,
        });
    };

    const writeOther = (next: string) => {
        setLocalOtherText(next);
        onAnswerChange?.({...value, customText: next});
    };

    const options = question.options ?? [];
    const optionValueAt = (index: number) => options[index]?.id ?? `option-${index}`;
    const placeholder = localize(question.placeholder) || t('Select an option');
    const otherPlaceholder = question.otherPlaceholder?.[language]?.trim() || t('Please specify');

    const labelForValue = (entry: string) => {
        if (entry === OTHER_VALUE) return t('Other');
        const index = options.findIndex((_option, optionIndex) => optionValueAt(optionIndex) === entry);
        return index >= 0 ? localize(options[index].label) : '';
    };

    const menuItemSx = (selected: boolean) => (theme: Theme) => ({
        gap: 2,
        boxSizing: 'border-box' as const,
        minHeight: MENU_ROW_HEIGHT,
        ...(selected && {
            '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
            },
            '&.Mui-selected:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.16),
            },
        }),
    });

    return (
        <Stack gap={1}>
            <FormControl fullWidth>
                <Select
                    value={selected}
                    displayEmpty
                    onChange={(event) => select(event.target.value)}
                    renderValue={(entry) => (entry
                        ? labelForValue(entry)
                        : <Box component={'span'} sx={{color: 'text.secondary'}}>{placeholder}</Box>)}
                    inputProps={{'aria-label': surveyMarkdownToPlainText(localize(question.title)) || undefined}}
                >
                    <MenuItem value={''} sx={menuItemSx(false)}>{placeholder}</MenuItem>

                    {options.map((option, index) => {
                        const entry = optionValueAt(index);
                        const active = selected === entry;

                        return (
                            <MenuItem key={entry} value={entry} sx={menuItemSx(active)}>
                                <Box component={'span'} sx={{flex: 1, minWidth: 0}}>
                                    {localize(option.label)}
                                </Box>
                                {active && (
                                    <CheckIcon aria-hidden fontSize={'small'} sx={{color: theme => surveyControls(theme).accent, flexShrink: 0}}/>
                                )}
                            </MenuItem>
                        );
                    })}

                    {!!question.allowOther && (
                        <MenuItem value={OTHER_VALUE} sx={menuItemSx(selected === OTHER_VALUE)}>
                            <Box component={'span'} sx={{flex: 1, minWidth: 0}}>{t('Other')}</Box>
                            {selected === OTHER_VALUE && (
                                <CheckIcon aria-hidden fontSize={'small'} sx={{color: theme => surveyControls(theme).accent, flexShrink: 0}}/>
                            )}
                        </MenuItem>
                    )}
                </Select>
            </FormControl>

            {selected === OTHER_VALUE && (
                <Box>
                    <TextField
                        fullWidth
                        autoFocus
                        multiline
                        minRows={1}
                        maxRows={8}
                        value={otherText}
                        onChange={(event) => writeOther(event.target.value)}
                        placeholder={otherPlaceholder}
                        inputProps={{maxLength: OTHER_MAX_LENGTH}}
                    />

                    {otherText.length >= OTHER_COUNTER_FROM && (
                        <Typography variant={'body2'} color={'text.secondary'} sx={{mt: 0.5, textAlign: 'right'}}>
                            {`${formatCount(otherText.length, language)} / ${formatCount(OTHER_MAX_LENGTH, language)}`}
                        </Typography>
                    )}
                </Box>
            )}
        </Stack>
    );
}

export default DropdownQuestion;
